import type { HeatMapThematic } from "@esWebAPI/es/model/Models";
import type { Expression } from "azure-maps-control";

// TODO: have merged def from UI code (Style_Thematic_HotSpot.js) with the expression def but could possibly optimise each def to reduce repitition
const HEATMAP_DEFS = [
    {
        type : "interpolate",
        colors : ["rgba(0,0,255,0.7)", "rgba(255,255,0,0.7)", "rgba(255,0,0,0.7)"],
        info: [
            { color: "rgba(0,0,255,0.7)", description: "1%: Blue (Very low density)" },
            { color: "rgba(255,255,0,0.7)", description: "50%: Yellow (Moderate density)" },
            { color: "rgba(255,0,0,0.7)", description: "100%: Red (High density)" }
        ],
        expression: [
            "interpolate",
            ["linear"],
            ["heatmap-density"],
            0, "rgba(0,0,255,0)", // Fully transparent at density 0
            0.01, "rgba(0,0,255,{{ALPHA}})",
            0.5, "rgba(255,255,0,{{ALPHA}})",
            1, "rgba(255,0,0,{{ALPHA}})"
        ]
    },
    {
        type : "interpolate",
        colors : ["rgba(0,0,255,0.7)", "rgba(0,0,255,0.7)", "rgba(255,255,0,0.7)", "rgba(255,255,0,0.7)", "rgba(255,0,0,0.7)", "rgba(255,0,0,0.7)"],
        info : [
            {color: "rgba(0,0,255,0.7)", description: "0% - 10%: Blue (Very low density)"},
            {color:"rgba(255,255,0,0.7)", description: "33% - 66%: Yellow (Moderate density)"},
            {color:"rgba(255,0,0,0.7)", description: "90% - 100%: Red (High density)"}
        ],
        expression: [
            "interpolate",
            ["linear"],
            ["heatmap-density"],
            0, "rgba(0,0,255,0)", // Fully transparent at density 0
            0.01, "rgba(0,0,255,{{ALPHA}})",
            0.1, "rgba(0,0,255,{{ALPHA}})",
            0.33, "rgba(255,255,0,{{ALPHA}})",
            0.666, "rgba(255,255,0,{{ALPHA}})",
            0.9, "rgba(255,0,0,{{ALPHA}})",
            1, "rgba(255,0,0,{{ALPHA}})"
        ]
    },
    {
        expression: [
            "step",
            ["heatmap-density"],
            "rgba(0,0,255,0)", // Fully transparent at density 0
            0.01, "rgba(0,0,255,{{ALPHA}})",
            0.33, "rgba(255,255,0,{{ALPHA}})",
            0.66, "rgba(255,0,0,{{ALPHA}})"
        ],
        type : "step",
        colors : ["rgba(0,0,255,0.7)", "rgba(255,255,0,0.7)", "rgba(255,0,0,0.7)"],
        info : [
            {color: "rgba(0,0,255,0.7)", description: "1% - 33%: Blue (Very low density)"},
            {color:"rgba(255,255,0,0.7)", description: "33% - 66%: Yellow (Moderate density)"},
            {color:"rgba(255,0,0,0.7)", description: "66% - 100%: Red (High density)"}
        ]
    },
    {
        expression: [
            "interpolate",
            ["linear"],
            ["heatmap-density"],
            0, "rgba(0,0,255,0)", // Fully transparent at density 0
            0.01, "rgba(65,105,255,{{ALPHA}})", // Royal Blue
            0.3, "rgba(0,255,255,{{ALPHA}})",    // Cyan
            0.5, "rgba(50,205,50,{{ALPHA}})",    // Lime
            0.7, "rgba(255,255,0,{{ALPHA}})",    // Yellow
            1, "rgba(255,0,0,{{ALPHA}})"         // Red
        ],
        type : "interpolate",
        colors : ["rgba(65,105,255,0.7)", "rgba(0,255,255,0.7)", "rgba(50,205,50,0.7)", "rgba(255,255,0,0.7)", "rgba(255,0,0,0.7)"],
        info : [
            {color: "rgba(65,105,255,0.7)", description: "1%: Royal Blue (Very low density)"},
            {color: "rgba(0,255,255,0.7)", description: "30%: Cyan (Low density)"},
            {color: "rgba(50,205,50,0.7)", description: "50%: Lime (Moderate density)"},
            {color:"rgba(255,255,0,0.7)", description: "70%: Yellow (High density)"},
            {color:"rgba(255,0,0,0.7)", description: "100%: Red (Maximum density)"}
        ]
    },
    {
        expression: [
            "step",
            ["heatmap-density"],
            "transparent",
            0.01, "rgba(65,105,255,{{ALPHA}})", // Royal Blue
            0.2, "rgba(0,255,255,{{ALPHA}})",   // Cyan
            0.4, "rgba(50,205,50,{{ALPHA}})",   // Lime
            0.6, "rgba(255,255,0,{{ALPHA}})",   // Yellow
            0.8, "rgba(255,0,0,{{ALPHA}})"      // Red
        ],
        type : "step",
        colors : ["rgba(65,105,255,0.7)", "rgba(0,255,255,0.7)", "rgba(50,205,50,0.7)", "rgba(255,255,0,0.7)", "rgba(255,0,0,0.7)"],
        info : [
            {color: "rgba(65,105,255,0.7)", description: "1% - 20%: Royal Blue (Low density)"},
            {color: "rgba(0,255,255,0.7)", description: "20% - 40%: Cyan (Moderate density)"},
            {color: "rgba(50,205,50,0.7)", description: "40% - 60%: Lime (Moderate-High density)"},
            {color:"rgba(255,255,0,0.7)", description: "60% - 80%: Yellow (High density)"},
            {color:"rgba(255,0,0,0.7)", description: "80% - 100%: Red (Maximum density)"}
        ]
    }
];

export class HeatMapThematicStyler {
    private readonly heatmapThematic: HeatMapThematic;

    constructor(heatmapThematic: HeatMapThematic) {
        this.heatmapThematic = heatmapThematic;
    }

    public static getHeatmapDef(index = 0): any {
        if (index < 0 || index >= HEATMAP_DEFS.length) {
            throw new Error("Invalid heatmap gradient index");
        }
        return es.util.object.deepCopy(HEATMAP_DEFS[index]);
    }

    public static getAllHeatmapDef(): any {
        return es.util.object.deepCopy(HEATMAP_DEFS);
    }

    public getColorExpression(index = 0): Expression {
        if (index < 0 || index >= HEATMAP_DEFS.length) {
            throw new Error("Invalid heatmap gradient index");
        }
        const def = HeatMapThematicStyler.getHeatmapDef(index);
        const alpha = (1 - this.heatmapThematic.transparency / 100).toFixed(2);
        const gradient = es.util.object.deepCopy(def.expression);
        for (let i = 0; i < gradient.length; i++) {
            if (typeof gradient[i] === "string" && gradient[i].startsWith("rgba(")) { // TODO: bit hacky but will do for now
                gradient[i] = gradient[i].replace("{{ALPHA}}", alpha);
            }
        }
        return gradient as Expression;
    }
}